import { useEffect, useState } from "react";
import { MapPin, Plus, Trash2, UserCheck, Users, ChevronRight, Clock, Calendar } from 'lucide-react';
import { getCoaches, getKids } from "../api";
import { pageWrapper, card, input, btnPrimary, btnOutline } from "../components/UI";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import axios from "axios";

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api" });
api.interceptors.request.use(c => {
  const u = JSON.parse(localStorage.getItem("user"));
  if (u?.access_token) c.headers.Authorization = `Bearer ${u.access_token}`;
  return c;
});

const getLocations      = ()       => api.get("/locations");
const createLocation    = (d)      => api.post("/locations", d);
const deleteLocation    = (id)     => api.delete(`/locations/${id}`);
const createTemplate    = (d)      => api.post("/sessions/templates", d);
const updateTemplate    = (id, d)  => api.put(`/sessions/templates/${id}`, d);
const deleteTemplate    = (id)     => api.delete(`/sessions/templates/${id}`);
const updateEnrollments = (id, ks) => api.put(`/sessions/templates/${id}/enrollments`, { kid_ids: ks });

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const AGE_GROUPS = ["U5", "U7", "U10_12", "U14_16", "GIRLS"];
const AGE_GROUP_LABELS = { U5: "U5", U7: "U7", U10_12: "U10/12", U14_16: "U14/16", GIRLS: "Girls" };

const DAY_COLORS = {
  Monday:    "#00E5CC", Tuesday:  "#4DFFD2", Wednesday: "#FCD34D",
  Thursday:  "#F87171", Friday:   "#A78BFA", Saturday:  "#60A5FA", Sunday: "#FB923C",
};

// ── Small components ────────────────────────────────────────────────────────

function SessionRow({ t, coaches, onUpdateTemplate, onDelete, onManageStudents }) {
  const [editing, setEditing] = useState(false);
  const [coachIds, setCoachIds] = useState((t.coaches || []).map(c => c.id));
  const [details, setDetails] = useState({
    day_of_week: t.day_of_week, start_time: t.start_time, end_time: t.end_time, age_group: t.age_group,
  });
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleCoach = (id) => setCoachIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
  );

  const startEditing = () => {
    setCoachIds((t.coaches || []).map(c => c.id));
    setDetails({ day_of_week: t.day_of_week, start_time: t.start_time, end_time: t.end_time, age_group: t.age_group });
    setSaveError("");
    setEditing(true);
  };

  const save = async () => {
    if (details.end_time <= details.start_time) {
      setSaveError("End time must be after start time.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await onUpdateTemplate(t.id, { ...details, coach_ids: coachIds });
      setEditing(false);
    } catch (e) {
      setSaveError(e.response?.data?.detail || "Failed to save changes.");
    }
    setSaving(false);
  };

  const coachLabel = t.coaches?.length
    ? t.coaches.map(c => c.name).join(", ")
    : "Assign Coaches";

  return (
    <div style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
      className="rounded-xl p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ backgroundColor: `${DAY_COLORS[t.day_of_week]}20`, color: DAY_COLORS[t.day_of_week] }}
            className="px-2 py-0.5 rounded-full text-xs font-semibold">{t.day_of_week}</span>
          <span style={{ backgroundColor: "rgba(0,229,204,0.1)", color: "#00E5CC" }}
            className="px-2 py-0.5 rounded-full text-xs">{t.age_group}</span>
          <span className="text-gray-400 text-xs">{t.start_time} – {t.end_time}</span>
          <span className="text-gray-500 text-xs">· {t.session_enrollments?.length || 0} students</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => onManageStudents(t)}
            style={{ color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.08)" }}
            className="px-2 py-1 rounded-lg text-xs hover:text-white hover:border-white/20 transition-all">
            Students
          </button>
          <button onClick={() => editing ? setEditing(false) : startEditing()}
            style={{ color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.08)" }}
            className="px-2 py-1 rounded-lg text-xs hover:text-white hover:border-white/20 transition-all truncate max-w-[160px]">
            {editing ? "Editing…" : coachLabel}
          </button>
          <button onClick={() => onDelete(t.id)}
            style={{ color: "#F87171" }}
            className="px-2 py-1 rounded-lg text-xs hover:bg-red-500/10 transition-all">✕</button>
        </div>
      </div>

      {editing && (
        <div className="mt-3">
          {saveError && (
            <p style={{ color: "#F87171" }} className="text-xs mb-2">{saveError}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Day</label>
              <select style={{ ...input, backgroundImage: "none" }}
                className="w-full rounded-lg p-2 text-xs focus:outline-none"
                value={details.day_of_week} onChange={e => setDetails(d => ({ ...d, day_of_week: e.target.value }))}>
                {DAYS.map(d => <option key={d} style={{ backgroundColor: "#0D1F3C" }}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Start</label>
              <input style={input} type="time"
                className="w-full rounded-lg p-2 text-xs focus:outline-none"
                value={details.start_time} onChange={e => setDetails(d => ({ ...d, start_time: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">End</label>
              <input style={input} type="time"
                className="w-full rounded-lg p-2 text-xs focus:outline-none"
                value={details.end_time} onChange={e => setDetails(d => ({ ...d, end_time: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Age Group</label>
              <select style={{ ...input, backgroundImage: "none" }}
                className="w-full rounded-lg p-2 text-xs focus:outline-none"
                value={details.age_group} onChange={e => setDetails(d => ({ ...d, age_group: e.target.value }))}>
                {AGE_GROUPS.map(g => <option key={g} value={g} style={{ backgroundColor: "#0D1F3C" }}>{AGE_GROUP_LABELS[g]}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {coaches.length === 0 && (
              <span className="text-gray-600 text-xs">No coaches available</span>
            )}
            {coaches.map(c => {
              const selected = coachIds.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggleCoach(c.id)}
                  style={selected
                    ? { backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC", border: "1px solid rgba(0,229,204,0.4)" }
                    : { backgroundColor: "rgba(255,255,255,0.04)", color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.08)" }}
                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-all">
                  {c.name}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} style={btnPrimary}
              className="px-4 py-2 rounded-lg text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(false)}
              style={btnOutline} className="px-3 py-2 rounded-lg text-xs transition-all">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Location tile (expanded view) ──────────────────────────────────────────

function LocationTile({ loc, coaches, allKids, onRefresh, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [form, setForm] = useState({ day_of_week: "Monday", start_time: "", end_time: "", age_group: "U7", coach_ids: [] });
  const [formError, setFormError] = useState("");
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [selectedKids, setSelectedKids] = useState([]);
  const [enrollSearch, setEnrollSearch] = useState("");
  const [otherLocSearch, setOtherLocSearch] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState(null);
  const [deleteLocationConfirm, setDeleteLocationConfirm] = useState(false);

  const templates = loc.session_templates || [];
  const totalStudents = templates.reduce((sum, t) => sum + (t.session_enrollments?.length || 0), 0);

  const toggleFormCoach = (id) => setForm(f => ({
    ...f,
    coach_ids: f.coach_ids.includes(id) ? f.coach_ids.filter(x => x !== id) : [...f.coach_ids, id]
  }));

  const handleAddSession = async (e) => {
    e.preventDefault();
    if (form.end_time <= form.start_time) {
      setFormError("End time must be after start time.");
      return;
    }
    setFormError("");
    try {
      await createTemplate({ ...form, location_id: loc.id });
      setForm({ day_of_week: "Monday", start_time: "", end_time: "", age_group: "U7", coach_ids: [] });
      setShowAddSession(false);
      onRefresh();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to add session.");
    }
  };

  const handleUpdateTemplate = async (templateId, changes) => {
    await updateTemplate(templateId, changes);
    onRefresh();
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await deleteTemplate(templateId);
      onRefresh();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to remove session.");
    } finally {
      setDeleteTemplateTarget(null);
    }
  };

  const openEnroll = (t) => {
    setEnrollTarget(t);
    setEnrollSearch("");
    setOtherLocSearch("");
    setActionError("");
    const existing = t.session_enrollments?.map(e => e.kid_id) || [];
    if (existing.length > 0) {
      // Use saved enrollments
      setSelectedKids(existing);
    } else {
      // Auto-select all kids at this location with matching age group
      const autoSelected = allKids
        .filter(k => k.age_group === t.age_group && k.location_id === loc.id)
        .map(k => k.id);
      setSelectedKids(autoSelected);
    }
  };

  const saveEnroll = async () => {
    try {
      await updateEnrollments(enrollTarget.id, selectedKids);
      setEnrollTarget(null);
      onRefresh();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to save enrollment.");
    }
  };

  const toggleKid = (id) => setSelectedKids(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const eligibleKids = enrollTarget
    ? allKids.filter(k => k.age_group === enrollTarget.age_group && k.location_id === loc.id)
    : [];

  const visibleEligibleKids = eligibleKids.filter(k =>
    k.name.toLowerCase().includes(enrollSearch.toLowerCase())
  );

  // A kid's `location_id` is their one primary location, so a student who
  // genuinely trains at more than one place (e.g. a second location on a
  // different day) never shows up in eligibleKids above for that other
  // location's session. This lets an admin add them manually instead of
  // needing direct database access.
  const otherLocResults = enrollTarget && otherLocSearch.length > 1
    ? allKids.filter(k =>
        !eligibleKids.some(e => e.id === k.id) &&
        k.name.toLowerCase().includes(otherLocSearch.toLowerCase())
      )
    : [];

  const selectAllVisible = () => setSelectedKids(s => [
    ...s,
    ...visibleEligibleKids.filter(k => !s.includes(k.id)).map(k => k.id),
  ]);

  const deselectAllVisible = () => setSelectedKids(s =>
    s.filter(id => !visibleEligibleKids.some(k => k.id === id))
  );

  // Sort templates by day order
  const sortedTemplates = [...templates].sort((a, b) =>
    DAYS.indexOf(a.day_of_week) - DAYS.indexOf(b.day_of_week)
  );

  return (
    <>
      <div style={{ ...card, cursor: "pointer" }}
        className="rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all">
        {/* Tile header — always visible */}
        <div className="p-5" onClick={() => setExpanded(e => !e)}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
            <div style={{ backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC" }}
  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0">
  <MapPin size={22} strokeWidth={1.8} />
</div>
              <div>
                <h3 className="text-white font-semibold text-base">{loc.name}</h3>
                {loc.address && <p className="text-gray-500 text-xs mt-0.5">{loc.address}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right hidden sm:block">
                <p style={{ color: "#00E5CC" }} className="text-lg font-bold">{templates.length}</p>
                <p className="text-gray-500 text-xs">sessions/wk</p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-white text-lg font-bold">{totalStudents}</p>
                <p className="text-gray-500 text-xs">students</p>
              </div>
              <span style={{ color: expanded ? "#00E5CC" : "#6B7280", transform: expanded ? "rotate(180deg)" : "none", display: "inline-block" }}
                className="text-lg transition-transform">
                ›
              </span>
            </div>
          </div>

          {/* Day pills preview */}
          {!expanded && templates.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {sortedTemplates.map(t => (
                <span key={t.id}
                  style={{ backgroundColor: `${DAY_COLORS[t.day_of_week]}18`, color: DAY_COLORS[t.day_of_week] }}
                  className="px-2 py-0.5 rounded-full text-xs font-medium">
                  {t.day_of_week.slice(0,3)} {t.age_group}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Expanded content */}
        {expanded && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} className="p-5">
            {/* Session list */}
            {actionError && (
              <p style={{ color: "#F87171" }} className="text-xs mb-3">{actionError}</p>
            )}
            {sortedTemplates.length === 0 && !showAddSession && (
              <p className="text-gray-600 text-sm mb-4">No sessions yet for this location.</p>
            )}
            <div className="space-y-2 mb-4">
              {sortedTemplates.map(t => (
                <SessionRow key={t.id} t={t} coaches={coaches}
                  onUpdateTemplate={handleUpdateTemplate}
                  onDelete={() => setDeleteTemplateTarget(t)}
                  onManageStudents={openEnroll} />
              ))}
            </div>

            {/* Add session form */}
            {showAddSession ? (
              <form onSubmit={handleAddSession}
                style={{ backgroundColor: "rgba(0,229,204,0.04)", border: "1px solid rgba(0,229,204,0.15)" }}
                className="rounded-xl p-4">
                <p className="text-white text-sm font-semibold mb-3">New Fixed Session</p>
                {formError && (
                  <p style={{ color: "#F87171" }} className="text-xs mb-3">{formError}</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Day</label>
                    <select style={{ ...input, backgroundImage: "none" }}
                      className="w-full rounded-lg p-2.5 text-sm focus:outline-none"
                      value={form.day_of_week} onChange={e => setForm(f => ({ ...f, day_of_week: e.target.value }))}>
                      {DAYS.map(d => <option key={d} style={{ backgroundColor: "#0D1F3C" }}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Start</label>
                    <input style={input} type="time"
                      className="w-full rounded-lg p-2.5 text-sm focus:outline-none"
                      value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} required />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">End</label>
                    <input style={input} type="time"
                      className="w-full rounded-lg p-2.5 text-sm focus:outline-none"
                      value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} required />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Age Group</label>
                    <select style={{ ...input, backgroundImage: "none" }}
                      className="w-full rounded-lg p-2.5 text-sm focus:outline-none"
                      value={form.age_group} onChange={e => setForm(f => ({ ...f, age_group: e.target.value }))}>
                      {AGE_GROUPS.map(g => <option key={g} value={g} style={{ backgroundColor: "#0D1F3C" }}>{AGE_GROUP_LABELS[g]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-400 mb-1.5 block">Coaches</label>
                  <div className="flex flex-wrap gap-1.5">
                    {coaches.length === 0 && (
                      <span className="text-gray-600 text-xs">No coaches available — assign later</span>
                    )}
                    {coaches.map(c => {
                      const selected = form.coach_ids.includes(c.id);
                      return (
                        <button key={c.id} type="button" onClick={() => toggleFormCoach(c.id)}
                          style={selected
                            ? { backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC", border: "1px solid rgba(0,229,204,0.4)" }
                            : { backgroundColor: "rgba(255,255,255,0.04)", color: "#9CA3AF", border: "1px solid rgba(255,255,255,0.08)" }}
                          className="px-2.5 py-1 rounded-full text-xs font-medium transition-all">
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" style={btnPrimary}
                    className="px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all">
                    Add Session
                  </button>
                  <button type="button" onClick={() => setShowAddSession(false)} style={btnOutline}
                    className="px-4 py-2 rounded-lg text-sm transition-all">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowAddSession(true)} style={btnOutline}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all">
                + Add Session to this Location
              </button>
            )}

            {/* Remove location — only shown when expanded */}
            <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <button onClick={() => setDeleteLocationConfirm(true)}
                style={{ color: "#F87171", border: "1px solid rgba(248,113,113,0.2)" }}
                className="px-3 py-1.5 rounded-lg text-xs hover:bg-red-500/10 transition-all">
                Remove Location
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Enroll students modal */}
      {enrollTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }} onClick={() => setEnrollTarget(null)}>
          <div style={{ backgroundColor: "#0D1F3C", border: "1px solid rgba(0,229,204,0.25)", maxWidth: 440, width: "100%" }}
            className="rounded-2xl p-6 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-white font-semibold">Enrolled Students</h2>
              <span style={{ backgroundColor: "rgba(0,229,204,0.1)", color: "#00E5CC" }}
                className="px-2 py-0.5 rounded-full text-xs">{selectedKids.length} selected</span>
            </div>
            <p className="text-gray-500 text-xs mb-3">
              {loc.name} · {enrollTarget.day_of_week} {enrollTarget.age_group} · {eligibleKids.length} eligible students
            </p>

            <input style={input}
              className="w-full rounded-lg p-2.5 text-sm focus:outline-none mb-2"
              placeholder="Search students…"
              value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)} />

            <div className="flex gap-2 mb-3">
              <button onClick={selectAllVisible} style={btnOutline}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all">
                Select all{enrollSearch ? " (filtered)" : ""}
              </button>
              <button onClick={deselectAllVisible} style={btnOutline}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all">
                Deselect all{enrollSearch ? " (filtered)" : ""}
              </button>
            </div>

            <div className="overflow-y-auto flex-1 space-y-1.5 mb-4">
              {eligibleKids.length === 0 && (
                <p className="text-gray-600 text-sm text-center py-6">No {enrollTarget.age_group} students found.</p>
              )}
              {eligibleKids.length > 0 && visibleEligibleKids.length === 0 && (
                <p className="text-gray-600 text-sm text-center py-6">No students match "{enrollSearch}".</p>
              )}
              {visibleEligibleKids.map(k => {
                const sel = selectedKids.includes(k.id);
                return (
                  <button key={k.id} onClick={() => toggleKid(k.id)}
                    style={sel
                      ? { backgroundColor: "rgba(0,229,204,0.12)", border: "1px solid rgba(0,229,204,0.4)" }
                      : { backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left">
                    <div style={sel
                        ? { backgroundColor: "#00E5CC", color: "#0A1628" }
                        : { backgroundColor: "rgba(0,229,204,0.15)", color: "#00E5CC" }}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {sel ? "✓" : k.name.charAt(0)}
                    </div>
                    <span className="text-white text-sm">{k.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Add a student whose home location differs from this one --
                eligibleKids only matches a kid's single stored location. */}
            <div className="mb-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <p className="text-xs text-gray-500 pt-3 mb-2">
                Student trains here but their profile says a different location?
              </p>
              <input style={input}
                className="w-full rounded-lg p-2.5 text-sm focus:outline-none mb-2"
                placeholder="Search all students by name…"
                value={otherLocSearch} onChange={e => setOtherLocSearch(e.target.value)} />
              {otherLocResults.length > 0 && (
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {otherLocResults.map(k => {
                    const sel = selectedKids.includes(k.id);
                    return (
                      <button key={k.id} onClick={() => toggleKid(k.id)}
                        style={sel
                          ? { backgroundColor: "rgba(0,229,204,0.12)", border: "1px solid rgba(0,229,204,0.4)" }
                          : { backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                        className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg text-left transition-all">
                        <span className="text-white text-sm">{k.name}</span>
                        <span className="text-gray-500 text-xs">{sel ? "✓ added" : `${k.age_group}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {otherLocSearch.length > 1 && otherLocResults.length === 0 && (
                <p className="text-gray-600 text-xs">No match, or already in the list above.</p>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={saveEnroll} style={btnPrimary}
                className="flex-1 py-2.5 rounded-lg font-semibold text-sm hover:opacity-90 transition-all">Save</button>
              <button onClick={() => setEnrollTarget(null)} style={btnOutline}
                className="px-5 py-2.5 rounded-lg text-sm transition-all">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteTemplateTarget && (
        <ConfirmDeleteModal
          title={`Remove ${deleteTemplateTarget.day_of_week} ${deleteTemplateTarget.age_group} session?`}
          description={`${deleteTemplateTarget.start_time}–${deleteTemplateTarget.end_time} at ${loc.name}. This stops it from generating any further sessions -- already-marked attendance history is kept.`}
          onConfirm={() => handleDeleteTemplate(deleteTemplateTarget.id)}
          onCancel={() => setDeleteTemplateTarget(null)}
        />
      )}

      {deleteLocationConfirm && (
        <ConfirmDeleteModal
          title={`Remove ${loc.name}?`}
          description="This also removes every session template at this location. Already-marked attendance history is kept."
          onConfirm={async () => { await onDelete(loc.id); setDeleteLocationConfirm(false); }}
          onCancel={() => setDeleteLocationConfirm(false)}
        />
      )}
    </>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function Sessions() {
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [allKids, setAllKids] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [locForm, setLocForm] = useState({ name: "", address: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [locsRes, coachesRes, kidsRes] = await Promise.all([
      getLocations(), getCoaches(), getKids()
    ]);
    setLocations(locsRes.data);
    setCoaches(coachesRes.data);
    setAllKids(kidsRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAddLocation = async (e) => {
    e.preventDefault();
    await createLocation({ name: locForm.name, address: locForm.address || null });
    setLocForm({ name: "", address: "" });
    setShowAddLocation(false);
    load();
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm("Remove this location and all its sessions?")) return;
    await deleteLocation(id);
    load();
  };

  return (
    <div style={pageWrapper} className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Sessions</h1>
          <p className="text-gray-400 mt-1 text-sm">Tap a location to manage its fixed weekly sessions</p>
        </div>
        <button onClick={() => setShowAddLocation(f => !f)} style={btnPrimary}
          className="px-4 py-2 rounded-lg font-semibold text-sm hover:opacity-90 transition-all whitespace-nowrap">
          {showAddLocation ? "Cancel" : "Add Location"}
        </button>
      </div>

      {/* Add location form */}
      {showAddLocation && (
        <div style={card} className="rounded-2xl p-5 mb-6">
          <h2 className="font-semibold text-white mb-4">New Location</h2>
          <form onSubmit={handleAddLocation} className="flex flex-col sm:flex-row gap-3">
            <input style={input} className="flex-1 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
              placeholder="Location name (e.g. Colombo Ground)" value={locForm.name}
              onChange={e => setLocForm(f => ({ ...f, name: e.target.value }))} required />
            <input style={input} className="flex-1 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
              placeholder="Address (optional)" value={locForm.address}
              onChange={e => setLocForm(f => ({ ...f, address: e.target.value }))} />
            <button type="submit" style={btnPrimary}
              className="px-6 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition-all whitespace-nowrap">
              Add Location
            </button>
          </form>
        </div>
      )}

      {/* Location tiles */}
      {loading && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <div style={{ borderColor: "#00E5CC" }} className="animate-spin rounded-full h-4 w-4 border-b-2" />
          Loading…
        </div>
      )}

      {!loading && locations.length === 0 && (
        <div style={card} className="rounded-2xl p-12 text-center">
          <p className="text-4xl mb-3">📍</p>
          <p className="text-white font-semibold">No locations yet</p>
          <p className="text-gray-400 text-sm mt-1">Add your first training location above.</p>
        </div>
      )}

      <div className="space-y-4">
        {locations.map(loc => (
          <LocationTile
            key={loc.id}
            loc={loc}
            coaches={coaches}
            allKids={allKids}
            onRefresh={load}
            onDelete={handleDeleteLocation}
          />
        ))}
      </div>
    </div>
  );
}